// Flat-vs-per-seat subscription price math (Stage-1 Nigeria flat pricing).
//
// Mirrors backend SubscriptionPriceCalculator (Solo.Tenant.API/Services/Billing/
// SubscriptionPricingMath.cs):
//   per_seat: total = unit price x seats
//   flat:     total = flat base + max(0, seats - included) x extra-seat monthly rate
//             (extra seats are billed at 12x the monthly rate on annual cycles;
//              the annual discount applies to the flat base only)
//
// All amounts are unit-agnostic: pass major units to get major units back, minor
// units to get minor units back.

export type PlanPricingModel = 'per_seat' | 'flat';

export function isFlatModel(pricingModel: string | null | undefined): boolean {
  return (pricingModel ?? '').trim().toLowerCase() === 'flat';
}

export function normalizePricingModel(pricingModel: string | null | undefined): PlanPricingModel {
  return isFlatModel(pricingModel) ? 'flat' : 'per_seat';
}

export interface PlanCycleTotalInput {
  pricingModel: string | null | undefined;
  /** Per-seat: unit price per seat for the cycle. Flat: flat base price for the cycle. */
  basePrice: number;
  seats: number;
  includedSeats?: number | null;
  /** Flat only: MONTHLY rate per extra seat (even on annual cycles). */
  extraSeatMonthlyPrice?: number | null;
  billingPeriod: 'monthly' | 'annual';
}

export function computePlanCycleTotal(input: PlanCycleTotalInput): number {
  if (!isFlatModel(input.pricingModel)) {
    return input.basePrice * input.seats;
  }

  const included = input.includedSeats ?? 0;
  const extraSeats = Math.max(0, input.seats - included);
  const cycles = input.billingPeriod === 'annual' ? 12 : 1;
  return input.basePrice + extraSeats * (input.extraSeatMonthlyPrice ?? 0) * cycles;
}

/** Seats above the flat allowance (0 for per-seat plans or within-allowance). */
export function extraSeatCount(
  pricingModel: string | null | undefined,
  seats: number,
  includedSeats?: number | null
): number {
  if (!isFlatModel(pricingModel)) {
    return 0;
  }
  return Math.max(0, seats - (includedSeats ?? 0));
}
